import {
  ProjektdokumentationFormData,
  ProjektdokumentationSection,
  ProjektdokumentationChecklistItem,
  ProjektdokumentationValidationResult,
} from "../types/projektdokumentation";

const REQUIRED_SPECIFIC_ITEM_IDS = [
  "heating-load-calculation-available",
  "refrigerant-route-documented",
  "electrical-route-documented",
  "route-understandable",
  "positions-defined",
  "documentation-complete",
];

const TECHNICAL_QUESTIONS_STATUS_ITEM_ID = "technical-questions-status";
const TECHNICAL_QUESTIONS_STATUS_LABEL = "Keine offenen technischen Fragen";

interface MissingRequiredItem {
  id: string;
  label: string;
}

export const isChecklistItemCompleted = (
  item: ProjektdokumentationChecklistItem
): boolean => {
  if (!item.checked) {
    return false;
  }

  if (!item.requiresInput) {
    return true;
  }

  switch (item.inputType) {
    case "text":
    case "select":
      return !!item.valueString?.trim();
    case "url":
      return !!item.valueUrl?.trim();
    case "number":
      return typeof item.valueNumber === "number" && Number.isFinite(item.valueNumber);
    case "boolean":
      return typeof item.valueBoolean === "boolean";
    default:
      return true;
  }
};

export const isChecklistItemVisible = (
  data: ProjektdokumentationFormData,
  sectionId: string,
  itemId: string
): boolean => {
  const section = data.sections.find((entry) => entry.id === sectionId);
  const itemMap = new Map(section?.items.map((item) => [item.id, item]) || []);

  switch (itemId) {
    case "drain-documented":
      return itemMap.get("drain-availability-recorded")?.valueBoolean === true;
    case "low-ceiling-height-documented":
      return itemMap.get("low-ceiling-height-present")?.valueBoolean === true;
    case "special-challenges-structural":
    case "special-challenges-technical":
    case "special-challenges-organizational":
      return itemMap.get("special-challenges-documented")?.checked === true;
    default:
      return true;
  }
};

export const getSectionProgress = (
  dataOrSection: ProjektdokumentationFormData | ProjektdokumentationSection,
  maybeSection?: ProjektdokumentationSection
) => {
  const section = maybeSection || (dataOrSection as ProjektdokumentationSection);
  const data = maybeSection
    ? (dataOrSection as ProjektdokumentationFormData)
    : ({ sections: [section] } as ProjektdokumentationFormData);
  const visibleItems = section.items.filter((item) =>
    isChecklistItemVisible(data, section.id, item.id)
  );
  const completed = visibleItems.filter(isChecklistItemCompleted).length;
  return {
    completed,
    total: visibleItems.length,
    isComplete: visibleItems.length > 0 && completed === visibleItems.length,
  };
};

export const validateProjektdokumentation = (
  data: ProjektdokumentationFormData
): ProjektdokumentationValidationResult => {
  const visibleItems = data.sections.flatMap((section) =>
    section.items.filter((item) => isChecklistItemVisible(data, section.id, item.id))
  );
  const completedCount = visibleItems.filter(isChecklistItemCompleted).length;
  const completenessPercent =
    visibleItems.length === 0 ? 0 : Math.round((completedCount / visibleItems.length) * 100);

  const missingRequiredItemsById = new Map<string, MissingRequiredItem>();
  visibleItems
    .filter((item) => item.required && !isChecklistItemCompleted(item))
    .forEach((item) => {
      missingRequiredItemsById.set(item.id, { id: item.id, label: item.label });
    });

  for (const itemId of REQUIRED_SPECIFIC_ITEM_IDS) {
    const item = visibleItems.find((entry) => entry.id === itemId);
    if (item && !isChecklistItemCompleted(item)) {
      missingRequiredItemsById.set(item.id, { id: item.id, label: item.label });
    }
  }

  if (data.technicalQuestionsOpen) {
    missingRequiredItemsById.set(TECHNICAL_QUESTIONS_STATUS_ITEM_ID, {
      id: TECHNICAL_QUESTIONS_STATUS_ITEM_ID,
      label: TECHNICAL_QUESTIONS_STATUS_LABEL,
    });
  }

  const missingRequiredItems = Array.from(
    new Set(Array.from(missingRequiredItemsById.values()).map((item) => item.label))
  );

  return {
    completenessPercent,
    missingRequiredItems,
    missingRequiredItemIds: Array.from(missingRequiredItemsById.keys()),
    isReadyForApproval: missingRequiredItems.length === 0,
  };
};
